import React, { useEffect, useState, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { FaGithub, FaLinkedin, FaYoutube, FaEnvelope, FaPhone, FaMapMarkerAlt, FaAws, FaJava, FaReact, FaDatabase, FaCode } from 'react-icons/fa';
import { SiSpringboot, SiApachekafka, SiDocker, SiKubernetes } from 'react-icons/si';
import './App.css';

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const skillCategories = [
    {
      title: "Languages",
      skills: ["Java (Core/Advanced)", "JavaScript (ES6+)", "Python", "SQL", "HTML/CSS"],
      icon: <FaCode />
    },
    {
      title: "Backend Engineering",
      skills: ["Spring Boot", "Microservices", "RESTful APIs", "Distributed Systems", "Multithreading"],
      icon: <FaJava />
    },
    {
      title: "Frontend Development",
      skills: ["React.js", "Redux", "Component Lifecycle", "Responsive Design"],
      icon: <FaReact />
    },
    {
      title: "Data & Messaging",
      skills: ["Apache Kafka", "MySQL/PostgreSQL", "NoSQL", "Event-Driven Architecture"],
      icon: <FaDatabase />
    },
    {
      title: "Cloud & DevOps",
      skills: ["AWS (Certified)", "Docker", "Kubernetes", "Jenkins", "CI/CD Pipelines"],
      icon: <FaAws />
    }
  ];

  const experiences = [
    {
      company: "Tata Consultancy Services (TCS)",
      role: "Software Development Engineer",
      period: "Sep 2023 – Present",
      achievements: [
        "Re-architected legacy batch processing system into distributed solution, reducing workflow time by 95%",
        "Engineered high-availability, event-driven microservices pipeline using Apache Kafka",
        "Maintained 99.9% application uptime with robust CI/CD pipelines and JUnit testing"
      ]
    },
    {
      company: "Claysys Technologies",
      role: "RPA Developer",
      period: "Jun 2023 – Aug 2023",
      achievements: [
        "Designed workflow automation tools reducing manual reporting time by 30%",
        "Integrated disparate backend services to improve operational data visibility"
      ]
    },
    {
      company: "Freelancer",
      role: "Full Stack Developer",
      period: "Jun 2022 – Jun 2023",
      achievements: [
        "Architected academic management platform automating workflows by 40%",
        "Implemented secure Role-Based Access Control (RBAC) and OAuth2 integration",
        "Deployed applications on AWS with load balancers and auto-scaling"
      ]
    }
  ];

  const projects = [
    {
      title: "Real-Time Event Processing System",
      tech: ["Apache Kafka", "Java", "Spring Boot", "PostgreSQL"],
      description: "Designed a scalable event streaming pipeline that improved system throughput by 60%"
    },
    {
      title: "Railway Management System",
      tech: ["Java", "Spring Boot", "MySQL", "React.js"],
      description: "Developed concurrent reservation system with optimistic locking for data consistency"
    }
  ];

  return (
    <div className="portfolio-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-content">
          <motion.div 
            className="logo"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            RV
          </motion.div>
          <div className="nav-links">
            {['Home', 'About', 'Skills', 'Experience', 'Projects', 'Contact'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className={`nav-link ${activeSection === item.toLowerCase() ? 'active' : ''}`}
                onClick={() => setActiveSection(item.toLowerCase())}
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-text"
          >
            <h1 className="hero-title">
              <span className="gradient-text">RAHUL VIJAYAN</span>
            </h1>
            <h2 className="hero-subtitle">Full-Stack Software Engineer</h2>
            <p className="hero-description">
              Architecting scalable distributed systems & cloud-native applications with 3.5+ years of expertise in Java, Spring Boot, and React.js.
            </p>
            <div className="hero-buttons">
              <motion.a
                href="#contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="cta-button"
              >
                Get In Touch
              </motion.a>
              <motion.a
                href="#projects"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="secondary-button"
              >
                View Projects
              </motion.a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-image"
          >
            <div className="floating-card">
              <div className="card-content">
                <div className="tech-stack">
                  <FaJava className="tech-icon" />
                  <SiSpringboot className="tech-icon" />
                  <FaReact className="tech-icon" />
                  <SiApachekafka className="tech-icon" />
                  <FaAws className="tech-icon" />
                </div>
                <div className="achievement-badge">
                  <span>95%</span>
                  <small>Workflow Optimization</small>
                </div>
                <div className="achievement-badge">
                  <span>99.9%</span>
                  <small>Uptime</small>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="scroll-indicator"
        >
          ↓
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="section-content"
        >
          <motion.h2 variants={itemVariants} className="section-title">
            Professional Summary
          </motion.h2>
          <motion.div variants={itemVariants} className="about-content">
            <div className="about-text">
              <p>
                Full-Stack Software Engineer with 3.5 years of experience architecting and scaling distributed systems 
                and cloud-native applications. Expertise in Java, Spring Boot, and React.js, with a proven track record 
                of designing fault-tolerant microservices and high-throughput event-driven architectures (Apache Kafka).
              </p>
              <p>
                Successfully optimized legacy workflows to reduce execution time by 95%. Passionate about solving complex 
                concurrency and scalability challenges in mission-critical, high-volume environments.
              </p>
              <div className="contact-info">
                <div className="contact-item">
                  <FaPhone /> +91 9074023334
                </div>
                <div className="contact-item">
                  <FaEnvelope /> rahulvijayan2291@gmail.com
                </div>
                <div className="contact-item">
                  <FaMapMarkerAlt /> Trivandrum, Kerala, India
                </div>
              </div>
            </div>
            <div className="social-links">
              <motion.a 
                href="https://www.linkedin.com/in/rahul-vijayan-682a12194" 
                whileHover={{ y: -5 }}
                className="social-link"
              >
                <FaLinkedin /> LinkedIn
              </motion.a>
              <motion.a 
                href="https://github.com/Rahulvijayan2291" 
                whileHover={{ y: -5 }}
                className="social-link"
              >
                <FaGithub /> Portfolio
              </motion.a>
              <motion.a 
                href="https://www.youtube.com/@Rahul_Vijayan/" 
                whileHover={{ y: -5 }}
                className="social-link"
              >
                <FaYoutube /> YouTube
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="section dark-section">
        <div className="section-content">
          <h2 className="section-title">Technical Skills</h2>
          <div className="skills-grid">
            {skillCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="skill-category"
              >
                <div className="skill-header">
                  {category.icon}
                  <h3>{category.title}</h3>
                </div>
                <ul className="skill-list">
                  {category.skills.map((skill) => (
                    <li key={skill}>{skill}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="section">
        <div className="section-content">
          <h2 className="section-title">Professional Experience</h2>
          <div className="timeline">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.company}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="timeline-item"
              >
                <div className="timeline-content">
                  <div className="timeline-header">
                    <h3>{exp.company}</h3>
                    <span className="timeline-period">{exp.period}</span>
                  </div>
                  <h4 className="timeline-role">{exp.role}</h4>
                  <ul className="achievement-list">
                    {exp.achievements.map((achievement, i) => (
                      <li key={i}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section dark-section">
        <div className="section-content">
          <h2 className="section-title">Featured Projects</h2>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="project-card"
              >
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <div className="project-tech">
                    {project.tech.map((tech) => (
                      <span key={tech} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
                <p className="project-description">{project.description}</p>
                <div className="project-achievements">
                  <div className="achievement">
                    <span className="achievement-value">60%</span>
                    <span className="achievement-label">Throughput Improvement</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="section-content">
          <h2 className="section-title">Get In Touch</h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="contact-content"
          >
            <div className="contact-info-card">
              <h3>Let's Build Something Amazing</h3>
              <p>I'm currently open to new opportunities and interesting projects.</p>
              <div className="contact-details">
                <div className="contact-item">
                  <FaEnvelope />
                  <div>
                    <strong>Email</strong>
                    <p>rahulvijayan2291@gmail.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <FaPhone />
                  <div>
                    <strong>Phone</strong>
                    <p>+91 9074023334</p>
                  </div>
                </div>
                <div className="contact-item">
                  <FaMapMarkerAlt />
                  <div>
                    <strong>Location</strong>
                    <p>Trivandrum, Kerala, India</p>
                  </div>
                </div>
              </div>
              <div className="social-icons">
                <motion.a whileHover={{ scale: 1.2 }} href="https://www.linkedin.com/in/rahul-vijayan-682a12194/">
                  <FaLinkedin />
                </motion.a>
                <motion.a whileHover={{ scale: 1.2 }} href="https://github.com/Rahulvijayan2291">
                  <FaGithub />
                </motion.a>
                <motion.a whileHover={{ scale: 1.2 }} href="https://www.youtube.com/@Rahul_Vijayan/">
                  <FaYoutube />
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>© {new Date().getFullYear()} Rahul Vijayan. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default App;